import { useEffect, useState } from "react";
import { request } from "../../network/Request";
import Filter from "../../components/Filter/Filter.js";
import { useTranslation } from "react-i18next";
import { useYear } from "../../context/YearContext.js";
import StatusMessage from "../../components/StatusMessage/StatusMessage.js";

const MatchResultsView = ({
  matchType,
  event,
  gender,
  subTitle,
  topRankCount,
  winners, // 본선: 이긴 선수 번호 (없으면 상위 topRankCount 명 표시)
  mockScoreArray, // 테스트용: 주어지면 API 대신 이 데이터 사용
  maxRows, // 주어지면 위에서부터 이 인원까지만 표시 (동점자도 잘림)
  compact = false, // 전광판용: 검색, Player No., Event(회차별 점수) 칸 숨김
}) => {
  const { t } = useTranslation();
  const { year } = useYear();
  const [scoreArray, setScoreArray] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // Event 칸이 없으면 회차 헤더 줄도 없음
  const headRowSpan = compact ? 1 : 2;

  const getRankingClass = (index) => {
    return index < parseInt(topRankCount) ? "ranking" : "";
  };

  const shouldDisplayTop = () => {
    // 선택적으로 노란색 행 표시하는 매치타입 (8강, 4강, 3/4위전, 결승)
    let selectedRankingType = ["16", "8", "4", "3", "2"];
    return !winners || !selectedRankingType.includes(matchType);
  };

  // 승자 리스트에 있는지 확인
  const getWinnerClass = (winnerNumber) => {
    return winners.includes(String(winnerNumber)) ? "ranking" : "";
  };

  // 등수 계산 함수 (상위 16명만 동점 처리, 이후는 순차 등수)
  const calculateRank = (players) => {
    let currentRank = 1;

    return players.map((player, index) => {
      // 16명 이내: 동점 처리
      if (index < 16) {
        if (index > 0 && player.total !== players[index - 1].total) {
          currentRank = index + 1;
        }
      }
      // 16명 이후: 순차 등수
      else {
        currentRank = index + 1;
      }
      return {
        ...player,
        rank: currentRank,
      };
    });
  };

  useEffect(() => {
    setLoading(true);
    const fetchScores = mockScoreArray
      ? Promise.resolve({
          result: true,
          response: {
            score_array: mockScoreArray.map((item) => ({ ...item })),
          },
        })
      : request({
          "bk-working": "score-list",
          type: matchType === "PRE" ? matchType : matchType + "-" + event,
          distance: event,
          gender: gender,
          year: year,
        });

    fetchScores.then(({ result, response }) => {
      if (result) {
        let scoreArray = response["score_array"];

        // 각 선수 점수, 총점 계산
        scoreArray.forEach((item) => {
          let scoreMap = {};
          let total = 0;
          let turnArray = item.turn_concat.split(",");
          let scoreArr = item.score_concat.split(",");

          for (let i = 0; i < turnArray.length; i++) {
            scoreMap[turnArray[i]] = scoreArr[i];
            total += parseInt(scoreArr[i]);
          }

          item["score_map"] = scoreMap;
          item["total"] = total;
        });

        // 점수 기준 정렬
        const sorted = scoreArray.sort((a, b) => b.total - a.total);

        // 등수 계산
        const ranked = calculateRank(sorted);
        const withRank = maxRows ? ranked.slice(0, maxRows) : ranked;

        setScoreArray(withRank);
        setFiltered(withRank);
        console.log(withRank);
      } else {
        setScoreArray([]);
        setFiltered([]);
      }
      setLoading(false);
    });
  }, [matchType, event, gender, year, mockScoreArray, maxRows]);

  const handleSearch = (filters) => {
    let filtered = [...scoreArray];

    if (filters.country) {
      filtered = filtered.filter((p) => p.country === filters.country);
    }
    if (filters.playerName) {
      filtered = filtered.filter((p) =>
        p.player_name?.toLowerCase().includes(filters.playerName.toLowerCase())
      );
    }

    setFiltered(filtered);
  };

  return (
    <>
      <div className="table-box  box-1 margin-table">
        <div className="table-wrap result-table">
          <div className="table-shadow">
            <div className="table-title-big">
              - {event}m {subTitle} ({gender?.toUpperCase()}) -
            </div>
            {!compact && scoreArray.length !== 0 && (
              <Filter onSearch={handleSearch} />
            )}
            {loading ? (
              <StatusMessage type="loading" />
            ) : scoreArray.length === 0 ? (
              <StatusMessage />
            ) : filtered.length === 0 ? (
              <StatusMessage type="search" />
            ) : (
              <table>
                <colgroup>
                  <col width="5%" />
                  {!compact && <col width="5%" />}
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={headRowSpan}>{t("table.rank")}</th>
                    {!compact && (
                      <th rowSpan={headRowSpan}>
                        {t("positions.player")}
                        <br />
                        No.
                      </th>
                    )}
                    <th rowSpan={headRowSpan}>{t("table.country")}</th>
                    <th rowSpan={headRowSpan}>{t("table.player-name")}</th>
                    <th rowSpan={headRowSpan}>{t("common.gender")}</th>
                    {!compact && (
                      <th colSpan="5">
                        {t("table.event")} ({event}M)
                      </th>
                    )}
                    <th rowSpan={headRowSpan}>{t("table.total")}</th>
                  </tr>
                  {!compact && (
                    <tr>
                      <th>{t("table.turn")} 1</th>
                      <th>{t("table.turn")} 2</th>
                      <th>{t("table.turn")} 3</th>
                      <th>{t("table.turn")} 4</th>
                      <th>{t("table.turn")} 5</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {filtered.map((player, idx) => (
                    <tr
                      key={idx}
                      className={
                        shouldDisplayTop()
                          ? getRankingClass(idx)
                          : getWinnerClass(player.player_number)
                      }
                    >
                      <td>{player.rank}</td>
                      {!compact && <td>{player.player_number}</td>}
                      <td>{player.country}</td>
                      <td>{player.player_name}</td>
                      <td>{player.gender}</td>
                      {!compact && (
                        <>
                          <td>{player.score_map[1]}</td>
                          <td>{player.score_map[2]}</td>
                          <td>{player.score_map[3]}</td>
                          <td>{player.score_map[4]}</td>
                          <td>{player.score_map[5]}</td>
                        </>
                      )}
                      <td>{player.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-gradient"></div>
        </div>
      </div>
    </>
  );
};
export default MatchResultsView;
